import { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import SubmitLockButton from 'components/submit-lock-button/submit-lock-button.component'
import DriverCountStepper from './driver-count-stepper.component'
import DriversPlanIcon from './drivers-plan-icon.component'
import {
  DEFAULT_DRIVER_UNIT_PRICE,
  formatCadMonthly,
} from 'store/subscriptions/subscriptions.types'
import { ACCENT_BLUE } from 'utils/brand-colors'

type SubscribeDriversDialogProps = {
  open: boolean
  initialUnit?: number
  resubscribe?: boolean
  submitting?: boolean
  errorMessage?: string | null
  onClose: () => void
  onContinue: (unit: number) => void
}

const SubscribeDriversDialog = ({
  open,
  initialUnit = 1,
  resubscribe = false,
  submitting = false,
  errorMessage,
  onClose,
  onContinue,
}: SubscribeDriversDialogProps) => {
  const [unit, setUnit] = useState(1)

  useEffect(() => {
    if (open) {
      setUnit(Math.max(1, initialUnit))
    }
  }, [open, initialUnit])

  const total = useMemo(() => unit * DEFAULT_DRIVER_UNIT_PRICE, [unit])

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogContent sx={{ p: 4, position: 'relative' }}>
        <IconButton
          aria-label="Close"
          onClick={onClose}
          sx={{ position: 'absolute', top: 12, right: 12 }}
        >
          <CloseIcon />
        </IconButton>
        <Stack spacing={2.5} alignItems="center" textAlign="center">
          <DriversPlanIcon />
          <Box>
            <Typography variant="h5" fontWeight={700} color="#12355b">
              {resubscribe
                ? 'Resubscribe to Your Driver Plan'
                : 'Add Drivers to Your Account'}
            </Typography>
            <Typography mt={1} color="#6b7280">
              {resubscribe
                ? 'Your previous subscription has ended. Choose the number of drivers you need and continue to checkout.'
                : 'To add drivers, please subscribe to a plan. Choose the number of drivers you need and get started.'}
            </Typography>
          </Box>
          <Box width="100%">
            <Typography fontWeight={700} mb={1.5}>
              How many drivers do you need?
            </Typography>
            <DriverCountStepper value={unit} min={1} onChange={setUnit} />
          </Box>
          <Box
            width="100%"
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              bgcolor: '#eef6fb',
              borderRadius: '12px',
              py: 2,
            }}
          >
            <Box px={2}>
              <Typography color="#6b7280" fontSize={13}>
                Price per driver
              </Typography>
              <Typography fontWeight={700} mt={0.5}>
                {formatCadMonthly(DEFAULT_DRIVER_UNIT_PRICE)}
              </Typography>
            </Box>
            <Box px={2} sx={{ borderLeft: '1px solid #d5e4ef' }}>
              <Typography color="#6b7280" fontSize={13}>
                Total (Monthly)
              </Typography>
              <Typography fontWeight={700} mt={0.5}>
                {formatCadMonthly(total)}
              </Typography>
            </Box>
          </Box>
          <Box
            width="100%"
            sx={{
              display: 'flex',
              gap: 1.5,
              bgcolor: '#eef6fb',
              borderRadius: '12px',
              p: 1.5,
              textAlign: 'left',
            }}
          >
            <InfoOutlinedIcon sx={{ color: ACCENT_BLUE, mt: '2px' }} />
            <Typography color="#4b5563" fontSize={13}>
              You will be charged based on the number of drivers you select. You
              can increase or decrease the number of drivers at any time from
              your subscription settings.
            </Typography>
          </Box>
          {errorMessage && (
            <Typography color="error" fontSize={14}>
              {errorMessage}
            </Typography>
          )}
          <Stack direction="row" spacing={1.5} width="100%">
            <Button
              fullWidth
              variant="outlined"
              color="inherit"
              onClick={onClose}
              sx={{ py: 1.2, borderColor: '#d1d5db' }}
            >
              Cancel
            </Button>
            <SubmitLockButton
              fullWidth
              variant="contained"
              disabled={submitting || unit < 1}
              onClick={() => onContinue(unit)}
              sx={{ py: 1.2 }}
            >
              {submitting
                ? 'Starting...'
                : resubscribe
                  ? 'Continue to Resubscribe'
                  : 'Continue to Subscription'}
            </SubmitLockButton>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  )
}

export default SubscribeDriversDialog
